import React, { useMemo, useRef, useState } from 'react';

const SAMPLE_RATE = 24000; // PCM16 mono @ 24k

function toU8(b64) {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

function assembleWav(chunks) {
  const pcmLen = chunks.reduce((s, c) => s + c.length, 0);
  const header = new ArrayBuffer(44);
  const dv = new DataView(header);
  const W = (o, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
  const ch = 1, bits = 16, byteRate = SAMPLE_RATE * ch * bits / 8, blockAlign = ch * bits / 8;
  W(0, 'RIFF'); dv.setUint32(4, 36 + pcmLen, true);
  W(8, 'WAVE'); W(12, 'fmt ');
  dv.setUint32(16, 16, true); dv.setUint16(20, 1, true);
  dv.setUint16(22, ch, true); dv.setUint32(24, SAMPLE_RATE, true);
  dv.setUint32(28, byteRate, true); dv.setUint16(32, blockAlign, true);
  dv.setUint16(34, bits, true); W(36, 'data'); dv.setUint32(40, pcmLen, true);
  const pcm = new Uint8Array(pcmLen);
  let off = 0;
  for (const c of chunks) { pcm.set(c, off); off += c.length; }
  return new Blob([header, pcm], { type: 'audio/wav' });
}

export default function VoiceTest() {
  // use your existing proxy path; override in UI if needed
  const [proxyPath, setProxyPath] = useState('/ws/VoiceTest');
  const [model, setModel] = useState('gpt-realtime');
  const [voice, setVoice] = useState('alloy');
  const [say, setSay] = useState('hello there — this is a realtime test');
  const [state, setState] = useState('idle'); // 'idle' | 'connecting' | 'open' | 'closed' | 'error'

  const logRef = useRef(null);
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const pcmChunksRef = useRef([]);

  const wsUrl = useMemo(() => {
    const base = window.location.origin.replace(/^http/, 'ws');
    const url = new URL(base + proxyPath);
    url.searchParams.set('model', model);
    return url.toString();
  }, [proxyPath, model]);

  const log = (...args) => {
    const line = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
    if (logRef.current) {
      logRef.current.textContent += line + '\n';
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
    // eslint-disable-next-line no-console
    console.log('[voiceTest]', ...args);
  };

  const handleTest = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      log('[warn] already connected');
      return;
    }
    setState('connecting');
    log('[WS] connecting', wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    pcmChunksRef.current = [];

    ws.addEventListener('open', () => {
      setState('open');
      log('[open]');

      ws.send(JSON.stringify({
        type: 'session.update',
        session: {
          voice,
          input_audio_transcription: { enabled: true },
          instructions: 'You are a helpful assistant. Keep answers brief.'
        }
      }));

      ws.send(JSON.stringify({
        type: 'response.create',
        response: {
          output_modalities: ['audio'],
          instructions: `User says: "${say}".`
        }
      }));
    });

ws.addEventListener('message', (ev) => {
log('[RX.raw]', ev.data);
  let msg;
  try {
    msg = JSON.parse(ev.data);
  } catch {
    log('[rx non-json]');
    return;
  }

  // ✅ Smart log: truncate only audio.delta logs
  if (msg?.type === 'response.output_audio.delta' && typeof msg.delta === 'string') {
    //log('[RX:dump]', { ...msg, delta: msg.delta.slice(0, 250) + '…[truncated]' });
  } else {
    //log('[RX:dump]', { ...msg, delta: msg.delta.slice(0, 250) + '…[truncated]' });
    //log('[RX:dump]', msg);
  }

  // ✅ Main routing
  switch (msg.type) {
    case 'response.output_text.delta':
      log('[txt+=]', msg.delta || '');
      break;
    case 'response.output_audio_transcript.delta': {
    const text = msg.delta || '';
    console.log('[assistant transcript]', text);
    if (logRef.current) {
        const div = document.createElement('div');
        div.textContent = `🤖 ${text}`;
        logRef.current.appendChild(div);
        logRef.current.scrollTop = logRef.current.scrollHeight;
    }
    break;
    }


    case 'response.output_audio.delta': {
        const b64 = msg.delta || '';
        if (b64) pcmChunksRef.current.push(toU8(b64));

       // log('[audio+=]', b64.slice(0, 250));

      break;
    }

    case 'response.done': {
      log('[done]');
      if (pcmChunksRef.current.length) {
        const wav = assembleWav(pcmChunksRef.current);
        const src = URL.createObjectURL(wav);
        if (audioRef.current) {
          audioRef.current.src = src;
          audioRef.current.play().catch(() => {});
        }
      }
      break;
    }

    case 'error':
      log('[server error]', msg);
      break;

    default:

      log('[rx]', msg.type);
  }
});



    ws.addEventListener('close', (e) => { setState('closed'); log('[close]', e.code, e.reason || ''); });
    ws.addEventListener('error', (e) => { setState('error'); log('[error]', JSON.stringify(e)); });
  };

  const handleSecondTurn = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) { log('[warn] socket not open'); return; }
    ws.send(JSON.stringify({
      type: 'response.create',
      response: {
        output_modalities: ['audio'],
        instructions: 'Second turn: summarize your last answer in one sentence and say goodbye.'
      }
    }));
  };

  return (
    <div style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', padding: 16 , color: 'white' }}>
      <h1>ROBOT STUDIO GPT-REALTIME VOICE TEST </h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 900 }}>
        <label>
          Proxy WS path
          <input value={proxyPath} onChange={e => setProxyPath(e.target.value)} />
        </label>
        <label>
          Model (?model=)
          <input value={model} onChange={e => setModel(e.target.value)} />
        </label>
        <label>
          Voice
          <input value={voice} onChange={e => setVoice(e.target.value)} />
        </label>
        <label>
          Say
          <input style={{width: 250}} value={say} onChange={e => setSay(e.target.value)} />
        </label>
      </div>

      <div style={{ marginTop: 12 }}>
        <button onClick={handleTest} disabled={state === 'connecting'}>TEST</button>
        <button onClick={handleSecondTurn} style={{ marginLeft: 8 }}>Second Turn</button>
        <span style={{ marginLeft: 12 }}>state: {state}</span>
      </div>

      <fieldset style={{ marginTop: 16 }}>
        <legend>Client Socket Log</legend>
        <div id="socketLog" ref={logRef} style={{ whiteSpace: 'pre-wrap', background: '#0b1020', color: '#cfe3ff', padding: 12, minHeight: 220, borderRadius: 6 }} />
        <audio ref={audioRef} controls style={{ width: '100%', marginTop: 8 }} />
      </fieldset>
    </div>
  );
}
